import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Product } from "../entities/Product";
import { motion } from "framer-motion";

export default function ApparelPage() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    subcategory: "all",
    sortBy: "-created_date"
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [products, filters]);

  const loadProducts = async () => {
    setIsLoading(true);
    try {
      const data = await Product.filter({ category: "apparel" }, "-created_date", 50);
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    } finally {
      // Fixed: Always set loading to false, even on error
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];
    
    if (filters.subcategory !== "all") {
      filtered = filtered.filter(p => p.subcategory === filters.subcategory);
    }
    
    // Fixed: More robust sorting with null checks
    if (filters.sortBy === "price_asc") {
      filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (filters.sortBy === "price_desc") {
      filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
    }
    
    setFilteredProducts(filtered);
  };

  return (
    <div className="min-h-screen pb-16">
      <section className="page-wrap">
        <div className="panel relative overflow-hidden min-h-[370px]">
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/20 z-10" />
        <img 
          src="/apparelpic1.png"
          alt="Apparel"
          className="w-full h-full object-cover"
        />
          <div className="absolute inset-0 z-20 px-7 md:px-14 flex items-end pb-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
              className="text-white"
          >
              <p className="chip !text-white !border-white/40 !bg-white/10 mb-4">Fashion Collections</p>
            <h1 className="text-5xl md:text-7xl">Apparel</h1>
          </motion.div>
          </div>
        </div>
      </section>

      <section className="page-wrap mt-8">
        <div className="panel p-4 md:p-5 flex flex-col sm:flex-row sm:justify-end gap-3">
          <div className="relative min-w-[180px]">
            <select 
              value={filters.subcategory}
              onChange={(e) => setFilters(prev => ({ ...prev, subcategory: e.target.value }))}
                className="w-full appearance-none field-surface px-4 py-2.5 rounded-full focus:outline-none"
            >
              <option value="all">All Items</option>
              <option value="shirts">Shirts</option>
              <option value="jackets">Jackets</option>
              <option value="pants">Pants</option>
              <option value="accessories">Accessories</option>
            </select>
          </div>

          <div className="relative min-w-[210px]">
            <select 
              value={filters.sortBy}
              onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                className="w-full appearance-none field-surface px-4 py-2.5 rounded-full focus:outline-none"
            >
              <option value="-created_date">Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </div>
        </div>
      </section>

      <section className="page-wrap py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {isLoading ? (
            [...Array(6)].map((_, i) => (
                <div key={i} className="panel p-4 space-y-4">
                <div className="aspect-[4/5] bg-[var(--accent-soft)] animate-pulse rounded-xl" />
                <div className="h-4 w-2/3 bg-[var(--accent-soft)] animate-pulse rounded" />
                <div className="h-4 w-1/3 bg-[var(--accent-soft)] animate-pulse rounded" />
              </div>
            ))
          ) : (
            filteredProducts.map((product) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Link to={`/product/${product.id}`} className="block group">
                    <div className="panel relative aspect-[4/5] overflow-hidden bg-[var(--accent-soft)]">
                    {product.limited_edition && (
                        <div className="absolute top-4 left-4 z-10 bg-[var(--accent)] text-white px-3 py-1 text-xs tracking-[0.1em] uppercase rounded-full">
                        LIMITED EDITION
                      </div>
                    )}
                    <img 
                      src={product.images?.[0]}
                      alt={product.name}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    </div>
                    <h3 className="text-xl mt-4 mb-1 group-hover:opacity-70 transition-opacity">
                    {product.name}
                  </h3>
                    <p className="text-sm text-[var(--muted)]">${product.price?.toFixed(2)}</p>
                </Link>
              </motion.div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}